import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { ClubContext as ClubContextApi } from "@/api/entities";
import { fetchClubCurrency } from "@/api/control";
import { getCurrencyMeta } from "@/utils/currency";
import { zonedTimeToUtc as convertZonedToUtc } from "date-fns-tz";

const ClubCurrencyContext = createContext(null);
const DEFAULT_CURRENCY = { code: "GBP", symbol: "£" };
const DEFAULT_LOCALE = "en-GB";

function formatCurrencyAmount(amount, code, opts = {}) {
  const numeric = Number(amount);
  const safeValue = Number.isFinite(numeric) ? numeric : 0;
  const currencyCode =
    typeof code === "string" && code.trim() ? code.trim().toUpperCase() : DEFAULT_CURRENCY.code;
  const currencyDisplay = opts?.useCode ? "code" : "symbol";
  const locale = opts?.locale || DEFAULT_LOCALE;

  try {
    const formatter = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currencyCode,
      currencyDisplay,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return formatter.format(safeValue);
  } catch (err) {
    const fallback = new Intl.NumberFormat(DEFAULT_LOCALE, {
      style: "currency",
      currency: DEFAULT_CURRENCY.code,
      currencyDisplay,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return fallback.format(safeValue);
  }
}

function formatDateInTimeZone(date, timeZone, options) {
  if (!date) return "";
  const targetDate = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(targetDate?.getTime?.())) return "";

  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timeZone || "UTC",
    ...options,
  });

  return formatter.format(targetDate);
}

function formatDateKeyInZone(date, timeZone) {
  if (!date) return "";
  const value = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(value?.getTime?.())) return "";

  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: timeZone || "UTC",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  return formatter.format(value);
}

export function ClubCurrencyProvider({ clubCode, children }) {
  const [context, setContext] = useState({
    currency_code: DEFAULT_CURRENCY.code,
    currency_symbol: DEFAULT_CURRENCY.symbol,
    timezone: "UTC",
    timezone_source: null,
    timezone_fallback_used: false,
  });
  const [isLoading, setIsLoading] = useState(Boolean(clubCode));
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    if (!clubCode) {
      setIsLoading(false);
      return undefined;
    }

    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [clubResult, currencyPayload] = await Promise.all([
          ClubContextApi.getForClub(clubCode),
          fetchClubCurrency(clubCode),
        ]);
        const clubBody = clubResult?.data ?? clubResult;
        const clubData = clubBody?.data ?? clubBody;
        const currencyCode =
          (currencyPayload && typeof currencyPayload.code === "string" && currencyPayload.code) ||
          DEFAULT_CURRENCY.code;
        const currencySymbol =
          (currencyPayload && typeof currencyPayload.symbol === "string" && currencyPayload.symbol) ||
          getCurrencyMeta(currencyCode).symbol ||
          DEFAULT_CURRENCY.symbol;
        const timezone =
          (clubData && typeof clubData.timezone === "string" && clubData.timezone) || "UTC";
        const timezoneSource =
          (clubData && typeof clubData.timezone_source === "string" && clubData.timezone_source) || null;
        const timezoneFallbackUsed =
          (clubData && clubData.timezone_fallback_used === true) ||
          (!clubData?.timezone && timezone === "UTC");

        if (!isMounted) return;
        setContext({
          currency_code: currencyCode,
          currency_symbol: currencySymbol,
          timezone,
          timezone_source: timezoneSource,
          timezone_fallback_used: timezoneFallbackUsed,
        });
      } catch (err) {
        console.error("Failed to load club context:", err);
        if (!isMounted) return;
        setContext({
          currency_code: DEFAULT_CURRENCY.code,
          currency_symbol: DEFAULT_CURRENCY.symbol,
          timezone: "UTC",
          timezone_source: "fallback_utc",
          timezone_fallback_used: true,
        });
        setError(err);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [clubCode]);

  const meta = getCurrencyMeta(context.currency_code || DEFAULT_CURRENCY.code);

  const value = useMemo(() => {
    const currencyCode = context.currency_code || meta.code || DEFAULT_CURRENCY.code;
    const symbol = context.currency_symbol || meta.symbol || DEFAULT_CURRENCY.symbol;
    const timezone = context.timezone || "UTC";
    const timezoneSource = context.timezone_source || null;
    const timezoneFallbackUsed = context.timezone_fallback_used === true;

    return {
      code: currencyCode,
      symbol,
      locale: DEFAULT_LOCALE,
      timezone,
      timezoneSource,
      timezoneFallbackUsed,
      isLoading,
      error,
      format(amount, opts) {
        return formatCurrencyAmount(amount, currencyCode, { useCode: opts?.useCode, locale: opts?.locale });
      },
      formatInTimeZone(date, options) {
        return formatDateInTimeZone(date, timezone, options);
      },
      formatDateKey(date) {
        return formatDateKeyInZone(date, timezone);
      },
      zonedTimeToUtc(dateTimeString) {
        return convertZonedToUtc(dateTimeString, timezone);
      },
    };
  }, [context.currency_code, context.currency_symbol, context.timezone, context.timezone_fallback_used, context.timezone_source, error, isLoading, meta.code, meta.symbol]);

  return <ClubCurrencyContext.Provider value={value}>{children}</ClubCurrencyContext.Provider>;
}

export function useClubCurrency() {
  const ctx = useContext(ClubCurrencyContext);
  if (!ctx) {
    const fallbackMeta = getCurrencyMeta(DEFAULT_CURRENCY.code);
    return {
      code: DEFAULT_CURRENCY.code,
      symbol: DEFAULT_CURRENCY.symbol || fallbackMeta.symbol,
      locale: DEFAULT_LOCALE,
      timezone: "UTC",
      timezoneSource: "fallback_utc",
      timezoneFallbackUsed: true,
      isLoading: false,
      error: null,
      format(amount, opts) {
        return formatCurrencyAmount(amount, DEFAULT_CURRENCY.code, { useCode: opts?.useCode, locale: opts?.locale });
      },
      formatInTimeZone(date, options) {
        return formatDateInTimeZone(date, "UTC", options);
      },
      formatDateKey(date) {
        return formatDateKeyInZone(date, "UTC");
      },
      zonedTimeToUtc(dateTimeString) {
        return convertZonedToUtc(dateTimeString, "UTC");
      },
    };
  }
  return ctx;
}
